import React from "react";
import { useState, useRef, useEffect } from "react";
import {
    Box, Typography, Chip, Button, IconButton, Grid, TextField, Stack, Popper, Grow, Paper, ClickAwayListener, Tabs, Tab,
    Menu, MenuItem, Collapse
} from "@mui/material";
import moment from "moment";
import TableComponent from "components/grid";
import StatusBadge from "components/badge/StatusBadge";
import CustomPagination from "components/CustomPagination";
import { ReactComponent as EditIcon } from "assets/icons/EditIcon.svg";
import { LuEye, LuRefreshCw, LuSearch } from "react-icons/lu";
import { BiListUl } from "react-icons/bi";
import { MdOutlineFileDownload, MdOutlineFilterAlt, MdDeleteOutline } from "react-icons/md";
import { FaPlus } from "react-icons/fa6";
import Select from "react-select";
import CustomButton from "components/Button";
import AddDeals from "./AddDeals";
import EditDeals from "./EditDeals";
import ViewDeals from "./ViewDeals";
import DeleteDeals from "./DeleteDeals";
import { useLazyGetDealsListQuery, useLazyGetExportDealsQuery } from "store/slices/apis/dealApiSlice";
import { useParams } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CalenderSVG from "assets/logo/Calendar";
import { DEALS_TRACKER_CREATE, DEALS_TRACKER_DELETE, DEALS_TRACKER_EDIT, DEALS_TRACKER_VIEW } from "helpers/permissionConfig";
import { usePermission } from "hooks/usePermission";
import { exportToExcel } from "utils/ExportToExcel";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";
import { useDispatch } from "store";
import ProjectLastUpdatedOn from "components/ProjectLastUpdatedOn";
import TrackerPageHeader from "components/TrackerPageHeader";
import { useGetLastUpdatedOnStatusQuery } from "store/slices/apis/projectStatusApiSlice";


const normalize = (value) =>
    (value || "")
        .toString()
        .trim()
        .toUpperCase();

const totalCount = 0;
const openCount = 0;
const closedCount = 0;

const DealsTracker = () => {

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);

    const [searchTerm, setSearchTerm] = useState("");

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleMenuOpen = (event, row) => {
        event.stopPropagation(); // prevent row click if any
        setMenuAnchorEl(event.currentTarget);
        setMenuRow(row);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuRow(null);
    };

    const handleMenuView = () => {
        if (menuRow) {
            handleViewClick(menuRow);
        }
        handleMenuClose();
    };

    const handleMenuEdit = () => {
        if (menuRow) {
            handleEditDeals(menuRow);
        }
        handleMenuClose();
    };
    const { projectId } = useParams();
    const { data: dealData } = useGetLastUpdatedOnStatusQuery({ ProjectId: projectId });

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortOrder, setSortOrder] = useState('desc');
    const [sortBy, setSortBy] = useState('CreatedDate');

    const [open, setOpen] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const anchorRef = useRef(null);
    const [isAddDeals, setIsAddDeals] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedData, setSelectedData] = useState(null);
    const [isView, setIsView] = useState(false);
    const [viewData, setViewData] = useState(null);
    const [isDelete, setIsDelete] = useState(false);
    const [deleteData, setDeleteData] = useState(null);

    const createAccess = usePermission(DEALS_TRACKER_CREATE);
    const editAccess = usePermission(DEALS_TRACKER_EDIT);
    const viewAccess = usePermission(DEALS_TRACKER_VIEW);
    const deleteAccess = usePermission(DEALS_TRACKER_DELETE);

    const dispatch = useDispatch();
    const [filters, setFilters] = useState({
        SortBy: sortBy,
        SortOrder: sortOrder,
        PageNumber: pageNumber,
        PageSize: pageSize,
        ProjectId: projectId,
        SearchTerm: "",
    });

    const [trigger, { data: dealsList, isFetching }] = useLazyGetDealsListQuery();
    const [exportTrigger, { isFetching: isExporting }] = useLazyGetExportDealsQuery();

    const ALL_EXPORT_COLUMNS = [
        { name: "ID", field: "customId" },
        { name: "Lead Name", field: "leadName" },
        { name: "Deal Name", field: "name" },
        { name: "Asset Type", field: "assetType" },
        { name: "Area In Sqft", field: "area" },
        { name: "LOI Signed Date", field: "loiSignedDate" },
        { name: "Model Rental Rate", field: "modelRentalRate" },
        { name: "Deal Rental Base", field: "dealRentalBase" },
        { name: "Deal Rental CAM", field: "dealRentalCam" },
        { name: "Deal Rental TI", field: "dealRentalTi" },
        { name: "Rental Comments", field: "rentalComments" },
        { name: "LCD", field: "lcd" },
        { name: "Rent Free Months", field: "rentFreeMonths" },
        { name: "RCD", field: "rcd" },
        { name: "Escalation", field: "escalation" },
        { name: "Notice Period (Days)", field: "noticePeriodDays" },
        { name: "Security Deposit Amount", field: "securityDeposit" },
        { name: "Utilities Amount", field: "utilities" },
        { name: "Stamp Duty (Lessee)", field: "stampDutyLessee" },
        { name: "Stamp Duty (Lessor)", field: "stampDutyLessor" },
        { name: "Handover Date", field: "handoverDate" },
        { name: "Lease To Handover (Days)", field: "leaseToHandoverDays" },
        { name: "Post Handover Escalations", field: "postHandoverEscalations" },
    ];

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    const handleExportExcel = async () => {
        try {
            const exportFilters = {
                ...filters,
                PageNumber: 1,
                PageSize: 5000,
            };

            const response = await exportTrigger(exportFilters).unwrap();
            const rows = response?.data ?? response ?? [];

            if (!Array.isArray(rows) || rows.length === 0) {
                openNotification("No data found to export.", "warning");
                return;
            }

            const isDateField = (field) => ["loiSignedDate", "lcd", "rcd", "handoverDate"].includes(field);

            const formattedData = rows.map((row, index) => {
                const rowData = { "S.No": index + 1 };

                ALL_EXPORT_COLUMNS.forEach((col) => {
                    const rawValue = row?.[col.field];
                    let value = rawValue ?? "N/A";

                    if (isDateField(col.field) && rawValue && moment(rawValue).isValid()) {
                        value = moment.utc(rawValue).format("DD MMM YYYY");
                    }

                    rowData[col.name] = value;
                });

                return rowData;
            });

            exportToExcel(formattedData, "Deals", "Deals Tracker");
            openNotification("Excel exported successfully", "success");
        } catch (error) {
            openNotification("Failed to export deals. Please try again.", "error");
        }
    };

    useEffect(() => {
        trigger(filters);
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm !== filters.SearchTerm) {
                const updatedFilters = {
                    ...filters,
                    SearchTerm: searchTerm,
                    PageNumber: 1,
                };
                setFilters(updatedFilters);
                trigger(updatedFilters);
                setPageNumber(1);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const handlePageChange = (newPage) => {
        setPageNumber(newPage);
        const updatedFilters = {
            ...filters,
            PageNumber: newPage,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPageNumber(1);
        const updatedFilters = {
            ...filters,
            PageSize: newSize,
            PageNumber: 1,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };

    const handleApplyFilters = () => {
        const updatedFilters = {
            ...filters,
            PageNumber: 1,
        };

        setFilters(updatedFilters);
        trigger(updatedFilters);
        setPageNumber(1);
        handleClose();
    };

    const handleReset = () => {
        const resetFilters = {
            ProjectId: projectId,
            SortBy: sortBy,
            SortOrder: sortOrder,
            PageNumber: 1,
            PageSize: 10,
            SearchTerm: "",
        };
        setFilters(resetFilters);
        trigger(resetFilters);
        setPageNumber(1);
        handleClose();
    };

    const handleSelectChange = (selectedOption, fieldName) => {
        const updatedFilters = {
            ...filters,
            [fieldName]: selectedOption?.value ?? null,
            PageNumber: 1,
        };

        setFilters(updatedFilters);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        const updatedFilters = {
            ...filters,
            [name]: value ? `${value}T00:00:00Z` : "",
            PageNumber: 1,
        };

        setFilters(updatedFilters);
    };

    const handleRequestSort = (property) => {
        const isAsc = sortBy === property && sortOrder === 'asc';
        const updatedFilters = {
            ...filters,
            SortBy: property,
            SortOrder: isAsc ? 'desc' : 'asc'
        };
        setFilters(updatedFilters);
        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortBy(property);
        trigger(updatedFilters);
    };

    const handleDelete = (filterType) => {
        const updatedFilters = {
            ...filters,
            [filterType]: "",
            PageNumber: 1
        };

        setFilters(updatedFilters);
        trigger(updatedFilters);
        setPageNumber(1);
    };

    const handleAddDeals = () => {
        setIsAddDeals((prev) => !prev);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleEditDeals = (row) => {
        setIsEdit((prev) => !prev);
        setSelectedData(row);
    };

    const handleEditDealsClose = () => {
        setIsEdit((prev) => !prev);
        setSelectedData(null);
    };

    const handleViewClick = (row) => {
        setIsView((prev) => !prev);
        setViewData(row);
    };

    const handleViewClose = () => {
        setIsView(false);
        setViewData(null);
    };

    const handleDeleteClick = (row) => {
        setIsDelete((prev) => !prev);
        setDeleteData(row);
    };

    const handleDeleteClose = () => {
        setIsDelete(false);
        setDeleteData(null);
    };

    const handleMenuDelete = () => {
        if (menuRow) {
            handleDeleteClick(menuRow);
        }
        handleMenuClose();
    };

    const highlightText = (text, highlight) => {
        if (!highlight || !text) return text;

        const regex = new RegExp(`(${highlight})`, 'gi');
        const parts = String(text).split(regex);

        return parts.map((part, index) =>
            regex.test(part) ? (
                <span
                    key={index}
                    style={{
                        backgroundColor: '#FFF3CD',
                        color: '#856404',
                        fontWeight: 'bold',
                        padding: '0 2px',
                        borderRadius: '3px'
                    }}
                >
                    {part}
                </span>
            ) : part
        );
    };

    const numericRenderer = (field) => (row) => {
        const value = row?.[field];

        if (!value && value !== 0) return "N/A";

        return Number(value).toLocaleString("en-IN");
    };

    const columns = [
        {
            name: "",
            field: "actions",
            width: "10px",
            sticky: true,
            renderer: (row) => (
                <>
                    {(viewAccess || editAccess || deleteAccess) &&
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, row)}
                            >
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    }
                </>
            ),
        },
        {
            name: "ID",
            field: "customId",
            width: "100px",
            sticky: true,
        },
        {
            name: "Lead Name",
            field: "leadName",
            width: "200px",
            sticky: true,
            renderer: (row) => row?.leadName ? row?.leadName : "N/A",
        },
        {
            name: "Deal Name",
            field: "name",
            width: "200px",
            sticky: true,
            renderer: (row) => row?.name ? highlightText(row.name, searchTerm) : "N/A",
        },
        {
            name: "Asset Type",
            field: "assetType",
            width: "250px",
            renderer: (row) => row?.assetType ? highlightText(row.assetType, searchTerm) : "N/A",
        },
        {
            name: "Area In Sqft",
            field: "area",
            width: "200px",
            renderer: numericRenderer("area"),
        },
        {
            name: "LOI Signed Date",
            field: "loiSignedDate",
            renderer: (row) => (
                row?.loiSignedDate ? moment.utc(row.loiSignedDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Model Rental Rate",
            field: "modelRentalRate",
            renderer: numericRenderer("modelRentalRate"),
        },
        {
            name: "Deal Rental Base",
            field: "dealRentalBase",
            renderer: numericRenderer("dealRentalBase"),
        },
        {
            name: "Deal Rental CAM",
            field: "dealRentalCam",
            renderer: numericRenderer("dealRentalCam"),
        },
        {
            name: "Deal Rental TI",
            field: "dealRentalTi",
            renderer: numericRenderer("dealRentalTi"),
        },
        {
            name: "Rental Comments",
            field: "rentalComments",
            width: "300px",
            renderer: (row) => row?.rentalComments ? row?.rentalComments : "N/A",
        },
        {
            name: "LCD",
            field: "lcd",
            renderer: (row) => (
                row?.lcd ? moment.utc(row.lcd).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Rent Free Months",
            field: "rentFreeMonths",
            renderer: (row) => row?.rentFreeMonths ?? "N/A",
        },
        {
            name: "RCD",
            field: "rcd",
            renderer: (row) => (
                row?.rcd ? moment.utc(row.rcd).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Escalation",
            field: "escalation",
            renderer: (row) => row?.escalation ? highlightText(row.escalation, searchTerm) : "N/A",
        },
        {
            name: "Notice Period (Days)",
            field: "noticePeriodDays",
            renderer: (row) => row?.noticePeriodDays ?? "N/A",
        },
        {
            name: "Security Deposite Amount",
            field: "securityDeposit",
            renderer: (row) => row?.securityDeposit ? row?.securityDeposit : "N/A",
        },
        {
            name: "Utilities Amount",
            field: "utilities",
           renderer: (row) => row?.utilities ? row?.utilities : "N/A",
        },
        {
            name: "Stamp Duty (Lesse)",
            field: "stampDutyLessee",
            renderer: (row) => row?.stampDutyLessee ? row?.stampDutyLessee : "N/A",
        },
        {
            name: "Stamp Duty (Lessor)",
            field: "stampDutyLessor",
            renderer: numericRenderer("stampDutyLessor"),
        },
        {
            name: "Handover Date",
            field: "handoverDate",
            renderer: (row) => (
                row?.handoverDate ? moment.utc(row.handoverDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Lease To Handover (Days)",
            field: "leaseToHandoverDays",
            renderer: (row) => row?.leaseToHandoverDays ?? "N/A",
        },
        {
            name: "Post Handover Esclations",
            field: "postHandoverEscalations",
            renderer: (row) => row?.postHandoverEscalations ? row?.postHandoverEscalations : "N/A",
        },
    ];

    return (
        <>
            <TrackerPageHeader title="Deals Tracker" description="Track leasing deals, commercial terms, and conversion progress.">
                <ProjectLastUpdatedOn date={dealData?.deals} />
            </TrackerPageHeader>
            <Grid container spacing={2} sx={{ p: 2 }}>

                <Grid size={12}>
                    <Box
                        sx={{
                            width: "100%",
                            borderBottom: "2px solid #ECECEC",
                            overflowX: "auto",
                            whiteSpace: "nowrap",
                            p: 0,
                            display: "flex", alignItems: "center", justifyContent: 'space-between',
                            "-webkit-overflow-scrolling": "touch",
                        }}
                    >
                        <Tabs
                            value={tabValue}
                            //onChange={handleTabChange}
                            TabIndicatorProps={{ style: { display: "none" } }}
                            variant="scrollable"
                            scrollButtons={false}
                            sx={{
                                justifyContent: "flex-start !important",
                                alignItems: "flex-start",
                                "& .MuiTabs-flexContainer": {
                                    justifyContent: "flex-start !important",
                                    gap: "30px",
                                },
                            }}
                        >
                            {["All"].map((label, index) => {
                                let count = 0;

                                if (label === "All") {
                                    count = totalCount;
                                }
                                else if (label === "Open") {
                                    count = openCount;
                                }
                                else if (label === "Closed") {
                                    count = closedCount;
                                }

                                return (
                                    <Tab
                                        key={index}
                                        label={
                                            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontSize: "14px",
                                                        lineHeight: "100%",
                                                        color: tabValue === index ? "#0E73F6" : "#7E7E7E",
                                                        fontFamily: "Outfit, sans-serif"
                                                    }}
                                                >
                                                    {label}
                                                </Typography>
                                                {index > 0 &&
                                                    <Chip
                                                        label={count}
                                                        sx={{
                                                            height: "20px",
                                                            fontSize: "14px",
                                                            fontWeight: 500,
                                                            lineHeight: 1,
                                                            backgroundColor: "#E5E9EB",
                                                            color: "#5F5F5F",
                                                        }}
                                                    />
                                                }

                                            </Box>
                                        }
                                        sx={{
                                            textTransform: "none",
                                            minWidth: "auto",
                                            paddingBottom: "14px",
                                            paddingLeft: 0,
                                            paddingRight: 0,
                                            borderBottom: tabValue === index ? "2px solid #0E73F6" : "2px solid transparent",
                                        }}
                                    />
                                );
                            })}
                        </Tabs>

                        <CustomButton
                            label="Create Deal"
                            startIcon={<FaPlus />}
                            color="primary"
                            onClick={handleAddDeals}
                            sx={{
                                backgroundColor: "#2563EB",
                                fontFamily: 'Outfit',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '100%',
                                letterSpacing: '0px',
                            }}
                        />

                    </Box>
                </Grid>

                <Grid size={12}>
                    <Grid container>
                        <Grid size={{ md: 8, sm: 6 }}>
                            <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                sx={{
                                    padding: "8px 0",
                                    alignItems: "center",
                                    minHeight: "40px"
                                }}
                            >

                                {filters.StartDateTo && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Start Date:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {moment(filters.StartDateTo).format('DD-MM-YYYY')}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('StartDateTo')}
                                    />
                                )}

                            </Stack>
                        </Grid>


                        <Grid size={{ md: 4, sm: 6 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { md: "row", xs: "column" },
                                    alignItems: { md: "center", xs: "flex-start" },
                                    justifyContent: 'end',
                                    p: "8px 0px"
                                }}
                            >
                                <TextField
                                    placeholder="Search By Name, Asset Type, Escalation ..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        startAdornment: <LuSearch style={{ marginRight: '8px', color: '#6B7280' }} />,
                                    }}
                                    sx={{
                                        minWidth: { md: '320px', xs: "auto" },
                                        // width: "100%",
                                        backgroundColor: '#fff',
                                        '& .MuiOutlinedInput-root': { borderRadius: '3px', minHeight: '36px', fontSize: '13px' },
                                        '& .MuiInputBase-input::placeholder': {
                                            fontSize: '0.9rem',
                                        }
                                    }}
                                    type="search"
                                />

                                {/* <IconButton
                                    ref={anchorRef}
                                    onClick={handleToggle}
                                >
                                    <MdOutlineFilterAlt size={24} color="#48535B" />
                                </IconButton> */}

                                <Popper
                                    open={open}
                                    anchorEl={anchorRef.current}
                                    placement="bottom-end"
                                    transition
                                    disablePortal={false}
                                    sx={{ zIndex: 2000 }}
                                >
                                    {({ TransitionProps }) => (
                                        <Grow {...TransitionProps}>
                                            <Paper
                                                sx={{
                                                    mt: 1,
                                                    p: 2,
                                                    borderRadius: 1,
                                                    boxShadow: "0px 4px 20px rgba(7, 7, 7, 0.08)",
                                                    width: 600,
                                                }}
                                            >
                                                <ClickAwayListener onClickAway={handleClose}>
                                                    <Box>
                                                        <Grid container spacing={2}>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Start Date</Typography>
                                                                <TextField
                                                                    type="date"
                                                                    name="StartDateTo"
                                                                    // onBlur={handleBlur}
                                                                    value={filters.StartDateTo ? new Date(filters.StartDateTo).toISOString().split('T')[0] : ""}
                                                                    onChange={handleInputChange}
                                                                    onClick={(e) => {
                                                                        // Trigger the native date picker
                                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                                        if (input) input.showPicker?.();
                                                                    }}
                                                                    fullWidth
                                                                    sx={{
                                                                        // ...rightTextStyle,
                                                                        "& input::-webkit-calendar-picker-indicator": {
                                                                            display: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& input[type='date']": {
                                                                            appearance: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& .MuiOutlinedInput-root": {
                                                                            "& fieldset": {
                                                                                border: "none",
                                                                            },
                                                                        },
                                                                    }}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}

                                                                            >
                                                                                {CalenderSVG}
                                                                            </Box>
                                                                        ),
                                                                        sx: {
                                                                            backgroundColor: "#E7E7EB",
                                                                            height: "40px",
                                                                            width: { xs: "100%", md: "100%" },
                                                                            borderRadius: "3px",
                                                                        },
                                                                    }}
                                                                />
                                                            </Grid>

                                                            <Grid size={12}>
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        justifyContent: "flex-end",
                                                                        alignItems: "center",
                                                                    }}
                                                                >
                                                                    <Button onClick={handleReset}
                                                                        sx={{
                                                                            textTransform: "none",
                                                                            color: "#F15744",
                                                                            fontSize: "14px",
                                                                            lineHeight: "100%",
                                                                            fontFamily: "Outfit, sans-serif",
                                                                            borderRadius: "3px",
                                                                            fontWeight: 500,
                                                                            px: "20px",
                                                                            py: "15px",
                                                                        }}>
                                                                        Reset
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        onClick={handleApplyFilters}
                                                                        variant="contained"
                                                                        sx={{
                                                                            backgroundColor: "#2563EB",
                                                                            borderRadius: "3px",
                                                                            textTransform: "none",
                                                                            fontWeight: 500,
                                                                            fontSize: "14px",
                                                                            lineHeight: "100%",
                                                                            fontFamily: "Outfit, sans-serif",
                                                                            color: "#FFFFFF",
                                                                            px: "20px",
                                                                            py: "15px",
                                                                        }}
                                                                    >
                                                                        Apply
                                                                    </Button>
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </ClickAwayListener>
                                            </Paper>
                                        </Grow>
                                    )}
                                </Popper>

                                <IconButton onClick={() => {
                                    trigger(filters);
                                }}>
                                    <LuRefreshCw size={20} color="#48535B" />
                                </IconButton>

                                <IconButton onClick={handleExportExcel} disabled={isExporting}>
                                    <MdOutlineFileDownload size={24} color="#48535B" />
                                </IconButton>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid size={12}>
                    <TableComponent
                        data={dealsList?.data || []}
                        isLoading={isFetching}
                        columns={columns}
                        order={sortOrder}
                        orderBy={sortBy}
                        createSortHandler={handleRequestSort}
                    />
                    <Menu
                        anchorEl={menuAnchorEl}
                        open={Boolean(menuAnchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                mt: 1,
                                borderRadius: "3px",
                                minWidth: 140,
                                border: "1px solid #E5E9EB",
                                boxShadow: "0px 10px 30px rgba(15, 23, 42, 0.12)",
                                overflow: "hidden",
                            },
                        }}
                        MenuListProps={{
                            sx: {
                                py: 0.5,
                            },
                        }}
                    >
                        {viewAccess &&
                            <MenuItem
                                onClick={handleMenuView}
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: "#111827",
                                    "&:hover": {
                                        backgroundColor: "#F6F8F9",
                                    },
                                }}
                            >
                                <LuEye size={16} style={{ color: "#4B5563" }} />
                                <span>View</span>
                            </MenuItem>
                        }

                        {editAccess &&
                            <MenuItem
                                onClick={handleMenuEdit}
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: "#111827",
                                    "&:hover": {
                                        backgroundColor: "#F6F8F9",
                                    },
                                }}
                            >
                                <EditIcon style={{ width: 14, height: 14 }} />
                                <span>Edit</span>
                            </MenuItem>
                        }

                        {deleteAccess &&
                            <MenuItem
                                onClick={handleMenuDelete}
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: "#111827",
                                    "&:hover": {
                                        backgroundColor: "#F6F8F9",
                                    },
                                }}
                            >
                                <MdDeleteOutline style={{ width: 14, height: 14 }} />
                                <span>Delete</span>
                            </MenuItem>
                        }
                    </Menu>

                    <CustomPagination
                        totalCount={dealsList?.totalRecords || 0}
                        pageSize={pageSize}
                        page={pageNumber}
                        onPageChange={handlePageChange}
                        setCurrentPage={setPageNumber}
                        onPageSizeChange={handlePageSizeChange}
                    />

                </Grid>
            </Grid >
            {isAddDeals && <AddDeals open={isAddDeals} handleClose={handleAddDeals} />}
            {isEdit && <EditDeals open={isEdit} handleClose={handleEditDealsClose} rowData={selectedData} />}
            {isView && <ViewDeals open={isView} handleClose={handleViewClose} rowData={viewData} />}
            {isDelete && <DeleteDeals open={isDelete} handleClose={handleDeleteClose} rowData={deleteData} />}
        </>
    );
}

export default DealsTracker;
